import Phaser from 'phaser';
import {
  ClientMessage,
  ServerMessage,
  ClientMessageType,
  ServerMessageType,
  Suit,
  PlayerPosition,
  AnimationEvent,
  AnimationSequenceId,
} from '@hokm/shared';

export class NetworkManager extends Phaser.Events.EventEmitter {
  private ws: WebSocket | null = null;
  private roomId: string | null = null;
  private playerId: string | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private wsUrl: string;

  constructor() {
    super();
    // Dynamically determine WebSocket URL based on current location
    this.wsUrl = this.determineWsUrl();
  }

  private determineWsUrl(): string {
    // Check for explicit environment variable first
    if (import.meta.env.VITE_WS_URL) {
      return import.meta.env.VITE_WS_URL;
    }

    const { hostname, protocol } = window.location;
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';

    // Production: hokm.truevoid.dev -> api.hokm.truevoid.dev
    if (hostname === 'hokm.truevoid.dev') {
      return `${wsProtocol}//api.hokm.truevoid.dev`;
    }

    // Cloudflare Pages dev/preview: *.pages.dev -> hokm-server.truevoid.workers.dev
    if (hostname.endsWith('.pages.dev')) {
      return 'wss://hokm-server.truevoid.workers.dev';
    }

    // Handle coder.truevoid.dev pattern: 8080--xxx -> 8787--xxx
    if (hostname.includes('--') && hostname.includes('.coder.')) {
      const serverHostname = hostname.replace(/^8080--/, '8787--');
      return `${wsProtocol}//${serverHostname}`;
    }

    // Default to localhost for local development
    return 'ws://localhost:8787';
  }

  async createRoom(playerCount: 2 | 3 | 4 = 4): Promise<string> {
    const apiUrl = this.wsUrl.replace('ws://', 'http://').replace('wss://', 'https://');
    const response = await fetch(`${apiUrl}/create-room?playerCount=${playerCount}`);
    const data = await response.json();
    return data.roomId;
  }

  connect(roomId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.roomId = roomId;
      const wsUrl = `${this.wsUrl}/room/${roomId}`;

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: ServerMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.emit('disconnected');
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case ServerMessageType.ROOM_JOINED:
        this.playerId = message.playerId;
        this.emit('roomJoined', message);
        break;

      case ServerMessageType.PLAYER_JOINED:
        this.emit('playerJoined', message);
        break;

      case ServerMessageType.PLAYER_LEFT:
        this.emit('playerLeft', message);
        break;

      case ServerMessageType.GAME_STATE:
        this.emit('gameState', message.state);
        break;

      case ServerMessageType.HAKEM_CARD_REVEALED:
        this.emit('hakemCardRevealed', message);
        break;

      case ServerMessageType.HAKEM_SELECTED:
        this.emit('hakemSelected', message);
        break;

      case ServerMessageType.PARTNER_SELECTED:
        this.emit('partnerSelected', message);
        break;

      case ServerMessageType.SEATS_SWAPPED:
        this.emit('seatsSwapped', message);
        break;

      case ServerMessageType.CARD_PLAYED:
        this.emit('cardPlayed', message);
        break;

      case ServerMessageType.TRUMP_SELECTED:
        this.emit('trumpSelected', message);
        break;

      case ServerMessageType.TRICK_WON:
        this.emit('trickWon', message);
        break;

      case ServerMessageType.HAND_COMPLETE:
        this.emit('handComplete', message);
        break;

      case ServerMessageType.GAME_OVER:
        this.emit('gameOver', message);
        break;

      case ServerMessageType.ERROR:
        this.emit('error', message);
        break;

      case ServerMessageType.PONG:
        this.emit('pong', message);
        break;

      case ServerMessageType.CARD_DEALT:
        this.emit('cardDealt', message);
        break;

      case ServerMessageType.DISCARD_REQUIRED:
        this.emit('discardRequired', message);
        break;

      case ServerMessageType.CARDS_DISCARDED:
        this.emit('cardsDiscarded', message);
        break;

      case ServerMessageType.STOCK_DRAW:
        this.emit('stockDraw', message);
        break;

      case ServerMessageType.PLAYER_DISCONNECTED:
        this.emit('playerDisconnected', message);
        break;

      case ServerMessageType.PLAYER_REPLACED_WITH_AI:
        this.emit('playerReplacedWithAI', message);
        break;

      case ServerMessageType.PLAYER_REMOVED:
        this.emit('playerRemoved', message);
        break;

      case ServerMessageType.KICKED:
        this.emit('kicked', message);
        break;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('reconnectFailed');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    // Emit reconnecting event so UI can show feedback
    this.emit('reconnecting', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delay,
    });

    setTimeout(() => {
      if (this.roomId) {
        console.log(`Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
        this.connect(this.roomId)
          .then(() => {
            // Successful reconnect - emit event so UI can recover
            this.emit('reconnected');
          })
          .catch(() => {
            // Will trigger onclose which calls attemptReconnect again
          });
      }
    }, delay);
  }

  private send(message: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  // Client actions
  joinRoom(playerName: string, playerId?: string): void {
    this.send({
      type: ClientMessageType.JOIN_ROOM,
      playerName,
      playerId,
    });
  }

  /**
   * Connect to a vanity room (creates if not exists).
   * Vanity rooms use the vanity name as the room ID.
   */
  async connectToVanityRoom(vanityName: string, playerCount: 2 | 3 | 4 = 4): Promise<void> {
    // Normalize vanity name: lowercase, alphanumeric + hyphen only
    const normalized = vanityName.toLowerCase().replace(/[^a-z0-9-]/g, '');

    // Try to initialize the room with the right player count (idempotent on server)
    const apiUrl = this.wsUrl.replace('ws://', 'http://').replace('wss://', 'https://');
    try {
      await fetch(`${apiUrl}/room/${normalized}/init?playerCount=${playerCount}`);
    } catch {
      // Room might already exist or server may not support this - that's fine
    }

    // Connect via WebSocket
    return this.connect(normalized);
  }

  leaveRoom(): void {
    this.send({
      type: ClientMessageType.LEAVE_ROOM,
    });
  }

  startGame(pointsToWin?: 3 | 7): void {
    this.send({
      type: ClientMessageType.START_GAME,
      pointsToWin,
    });
  }

  startDealing(): void {
    this.send({
      type: ClientMessageType.START_DEALING,
    });
  }

  selectTrump(suit: Suit): void {
    this.send({
      type: ClientMessageType.SELECT_TRUMP,
      suit,
    });
  }

  playCard(cardId: string): void {
    this.send({
      type: ClientMessageType.PLAY_CARD,
      cardId,
    });
  }

  discardCards(cardIds: string[]): void {
    this.send({
      type: ClientMessageType.DISCARD_CARDS,
      cardIds,
    });
  }

  addAI(position?: PlayerPosition): void {
    this.send({
      type: ClientMessageType.ADD_AI,
      position,
    });
  }

  removeAI(playerId: string): void {
    this.send({
      type: ClientMessageType.REMOVE_AI,
      playerId,
    });
  }

  replaceWithAI(playerId: string): void {
    this.send({
      type: ClientMessageType.REPLACE_WITH_AI,
      playerId,
    });
  }

  ping(): void {
    this.send({
      type: ClientMessageType.PING,
      timestamp: Date.now(),
    });
  }

  sendAnimationAck(event: AnimationEvent, sequenceId: AnimationSequenceId): void {
    this.send({
      type: ClientMessageType.ANIMATION_ACK,
      event,
      sequenceId,
    });
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    // Clear all state to allow fresh connections
    this.roomId = null;
    this.playerId = null;
    this.reconnectAttempts = 0;
  }

  getPlayerId(): string | null {
    return this.playerId;
  }

  getRoomId(): string | null {
    return this.roomId;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
